import React, { useEffect, useState } from 'react';
import LinkButton from '@sats-group/fresh-fitness-ui-lib/react/link-button';

import ContentContainer from 'root/client/components/content-container/content-container';
import CheckoutMembershipContent from 'root/client/components/checkout-membership-content/checkout-membership-content';
import apiHelper from 'root/client/ts/api-helper';
import SalesLayout from 'root/client/components/sales-layout/sales-layout';

import type { CheckoutMembership as Props } from './checkout-membership.types';
import type { CheckoutMembershipInteraction } from 'root/client/components/checkout-membership-content/checkout-membership-content.types';

const CheckoutMembership: React.FC<Props> = ({
  actions,
  content,
  errorMessages,
  information,
  layout,
  nextHref,
}) => {
  const [currentContent, setCurrentContent] = useState(content);
  const [dynamicMessages, setDynamicMessages] = useState<
    Parameters<typeof SalesLayout>[0]['dynamicMessages']
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    const resetNavigation = () => setIsNavigating(false);
    window.addEventListener('pageshow', resetNavigation);
    return () => window.removeEventListener('pageshow', resetNavigation);
  }, []);

  const handleInteraction = (hrefs: CheckoutMembershipInteraction) => {
    if (isLoading || isNavigating) {
      return;
    }

    setDynamicMessages([]);
    setIsLoading(true);
    apiHelper
      .get(hrefs.data)
      .then(content => {
        if (hrefs.redirect) {
          setIsNavigating(true);
          window.location.assign(hrefs.redirect);
          return;
        }

        setCurrentContent(content);
        window.history.replaceState(null, document.title, hrefs.gui);
      })
      .catch(
        apiHelper.overrideStatus({
          400: () => {
            setDynamicMessages([errorMessages[400]]);
            return true;
          },
          404: () => {
            setDynamicMessages([errorMessages[404]]);
            return true;
          },
        }),
      )
      .catch(() => {
        setDynamicMessages([errorMessages.generic]);
      })
      .finally(() => setIsLoading(false));
  };

  const handleNavigation: NonNullable<
    Parameters<typeof LinkButton>[0]['onClick']
  > = event => {
    if (isNavigating) {
      event.preventDefault();
    } else {
      setIsNavigating(true);
    }
  };

  return (
    <SalesLayout
      {...layout}
      dynamicMessages={dynamicMessages}
      isLoading={isLoading || isNavigating}
    >
      <div className="checkout-membership">
        <CheckoutMembershipContent
          {...currentContent}
          information={information}
          nextHref={nextHref}
          onInteraction={handleInteraction}
        />
        <ContentContainer width={ContentContainer.widths.Wide}>
          <div className="checkout-membership__actions">
            {actions.map(entry => (
              <div key={entry.href}>
                <LinkButton {...entry} onClick={handleNavigation} wide />
              </div>
            ))}
          </div>
        </ContentContainer>
      </div>
    </SalesLayout>
  );
};

export default CheckoutMembership;
